import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import SaveIcon from 'src/shared/assets/icons/checkmark.svg';
import UploadIcon from 'src/shared/assets/icons/cloud.svg';
import CloseIcon from 'src/shared/assets/icons/cross.svg';
import { Button } from 'src/shared/components/button/button';
import { useStore } from 'src/store';
import { PlanVersionType } from 'src/store/comparison/types';
import {
  GOPLAN_DRILLING_CARPET_PUBLISH_PUBLIC_DRAFT,
  GOPLAN_DRILLING_CARPET_PUBLISH_PERSONAL_DRAFT,
} from 'src/store/roles-store/roles';

import styles from './edit-mode-buttons.module.scss';

interface Props {
  onCancelEditing(): void;
  onSaveChanges(): void;
  onPublishChanges(): void;
  isDisabled: boolean;
  isLoading?: boolean;
}

export const EditModeButtons = observer(function EditModeButtons({
  onCancelEditing,
  onPublishChanges,
  onSaveChanges,
  isDisabled,
  isLoading,
}: Props) {
  const { t } = useTranslation();
  const { rolesStore, planVersionsService } = useStore();

  const isPublishingAvailable = useMemo(() => {
    const draft = planVersionsService.currentDraft;

    if (!draft) {
      return false;
    }

    const parentVersion = planVersionsService.versionsList.find((ver) => ver.id === draft.data.parentVersionId);

    if (!parentVersion) {
      return false;
    }

    if (parentVersion.data.versionType === PlanVersionType.public) {
      return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_PUBLISH_PUBLIC_DRAFT]);
    }

    if (parentVersion.data.versionType === PlanVersionType.personal) {
      return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_PUBLISH_PERSONAL_DRAFT]);
    }

    return false;
  }, [planVersionsService.currentDraft, planVersionsService.versionsList, rolesStore]);

  return (
    <div className={styles.container}>
      {isPublishingAvailable && (
        <Button
          className={styles.button}
          variant="primary"
          icon={<UploadIcon />}
          onClick={onPublishChanges}
          disabled={isDisabled}
          isLoading={isLoading}
        >
          {t('common:Buttons.toPublish')}
        </Button>
      )}
      <Button
        className={styles.button}
        variant="danger"
        icon={<CloseIcon />}
        onClick={onCancelEditing}
        disabled={isDisabled}
        isLoading={isLoading}
      >
        {t('common:Buttons.cancel')}
      </Button>
      <Button
        className={styles.button}
        variant="success"
        icon={<SaveIcon />}
        onClick={onSaveChanges}
        disabled={isDisabled}
        isLoading={isLoading}
      >
        {t('common:Buttons.save')}
      </Button>
    </div>
  );
});
